// Pure mapping from GET /api/tsp/dashboard onto the props the dashboard's
// section components render. No React and no I/O — every number here comes
// from the server; nothing is invented (see BookDistributionOverview's
// "Last Year" line: there is no stored sales target, so the comparison is
// to the same period last year, not a made-up quota).
import { formatNumber, initialsOf } from "../../utils/tspFormat";
import { colorForIndex } from "../../utils/tspColors";
import { DISTRIBUTION_METHOD_LABEL, DISTRIBUTION_METHOD_COLOR, RESPONSE_RATING_LABEL } from "../../constants/tspOperations";
import { stackPlacePoints } from "../journeys/coverageMapModel";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (key) => MONTH_SHORT[Number(key.slice(5, 7)) - 1];
const weekLabel = (key) => {
  const [, m, d] = key.split("-").map(Number);
  return `${MONTH_SHORT[m - 1]} ${d}`;
};

export function buildActiveJourneyBanner(activeJourney) {
  return activeJourney ? { label: "Journey Active", journeyName: activeJourney.name } : null;
}

export function buildStatCards(stats) {
  return [
    { key: "activeJourneys", label: "Active Journeys", value: String(stats.active_journeys), icon: "Compass" },
    { key: "booksDistributed", label: "Books Distributed", value: formatNumber(stats.books_distributed), icon: "BookOpen" },
    { key: "citiesCovered", label: "Cities Covered", value: String(stats.cities_covered), icon: "MapPin" },
    { key: "totalPlaces", label: "Total Places", value: formatNumber(stats.total_places ?? stats.cities_covered ?? 0), icon: "Map" },
    { key: "avgResponseScore", label: "Avg Response Score", value: stats.average_score !== null ? `${stats.average_score}/${stats.score_max}.0` : "—", icon: "Star" },
  ];
}

export function buildBookDistributionOverview(overview, stats) {
  const monthly = overview.monthly.map((row) => ({ period: monthLabel(row.key), actual: row.quantity, target: row.previous }));
  const weekly = overview.weekly.map((row) => ({ period: weekLabel(row.key), actual: row.quantity, target: row.previous }));
  const yearly = overview.yearly.map((row) => ({ period: row.key, actual: row.quantity }));

  const peakRow = monthly.reduce((best, row) => (row.actual > (best?.actual ?? -1) ? row : best), null);
  const peak = peakRow && peakRow.actual > 0 ? { x: peakRow.period, y: peakRow.actual, label: `Peak: ${formatNumber(peakRow.actual)}` } : null;

  return {
    monthly,
    weekly,
    yearly,
    peak,
    totals: {
      totalBooks: `${formatNumber(stats.books_distributed)} Books Distributed`,
      citiesCovered: `${stats.cities_covered} Cities Covered`,
      statesCovered: `${stats.states_covered} States Covered`,
    },
  };
}

export function buildDistributionByMethod(byMethod) {
  const total = byMethod.reduce((sum, row) => sum + row.quantity, 0);
  return byMethod.map((row) => ({
    name: DISTRIBUTION_METHOD_LABEL[row.method] || row.method,
    value: row.quantity,
    percent: total > 0 ? `${Math.round((row.quantity / total) * 100)}%` : "0%",
    color: DISTRIBUTION_METHOD_COLOR[row.method] || "#9AA3B2",
  }));
}

export function buildLiveTicker(liveTicker) {
  return liveTicker.map((row) => ({ team: row.team_name || "Unassigned", city: `${row.city_name}, ${row.state_name}`, books: row.books_today }));
}

export function buildBookWiseChart(bookWise) {
  const series = bookWise.years.map((year, index) => ({ key: year, name: year, color: colorForIndex(index) }));
  const data = bookWise.books.map((book) => ({ name: book.name, ...book.by_year }));
  return { data, series };
}

export function buildYearWiseDistribution(yearWise) {
  return yearWise.map((row) => ({ name: row.key, quantity: row.quantity }));
}

// City visits recorded per year (by visit date), the same years as Year Wise.
export function buildVisitWise(visitWise) {
  return (visitWise || []).map((row) => ({ name: row.key, visits: row.visits }));
}

export function buildActiveJourneys(activeJourneys) {
  return activeJourneys.map((journey, index) => ({
    id: journey.id,
    name: journey.name,
    team: journey.team_name || "Unassigned",
    city: journey.current_city || "—",
    progress: `${journey.visited_cities}/${journey.total_cities}`,
    daysLeft: journey.days_left === null ? "—" : journey.days_left < 0 ? "Overdue" : `${journey.days_left} day${journey.days_left === 1 ? "" : "s"} left`,
    initial: initialsOf(journey.team_name || journey.name).slice(0, 1),
    color: colorForIndex(index),
  }));
}

export function buildPublicResponseScore(publicResponse) {
  return {
    average: publicResponse.average_score ?? 0,
    max: publicResponse.score_max,
    label: publicResponse.label || "No responses yet",
    totalResponses: `${formatNumber(publicResponse.total)} total responses`,
    breakdown: publicResponse.breakdown.map((row) => ({
      label: RESPONSE_RATING_LABEL[row.rating],
      percent: row.percent,
      color: { EXCELLENT: "#2FA96A", VERY_GOOD: "#3D9BD0", GOOD: "#F3B01B", POOR: "#9F1D21" }[row.rating],
    })),
  };
}

export function buildStateWise(stateWise) {
  const rows = (list) =>
    list.map((row) => ({ rank: row.rank, state: row.state_name, books: formatNumber(row.books), journeys: formatNumber(row.journeys || 0), change: row.change }));
  return { monthly: rows(stateWise.monthly), yearly: rows(stateWise.yearly) };
}

// The journey map: GET /map/coverage's data, unfiltered (null without the
// journeys + city visits view permissions). Points come straight from the API —
// one per visited (or still-upcoming) place, listing every journey there — so
// no journey is merged away; only points with real coordinates are drawn.
// The dashboard map: one marker per JOURNEY PLACE with saved coordinates (the
// same points as the Journeys map; places at the very same spot share one
// marker that lists each of them). `placeCount` counts places, not markers.
export function buildJourneyLocations(journeyMap) {
  if (!journeyMap) return null;
  const places = (journeyMap.points || []).filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
  const located = new Set(places.flatMap((point) => point.journey_ids || []));
  const journeys = journeyMap.journeys || [];
  return {
    coverage: journeyMap,
    points: stackPlacePoints(places),
    journeyCount: journeys.length,
    locatedJourneyCount: journeys.filter((journey) => located.has(journey.id)).length,
    placeCount: places.length,
  };
}

export function buildRecentActivities(recentActivities) {
  return (recentActivities || []).map((item) => ({
    id: item.id,
    image: item.file_url || item.image,
    caption: item.caption || null,
    templeName: item.temple_name || item.templeName || null,
  }));
}
